import { useState } from "react";
import { ReservationLogEntryContainerDto, ReservationPayment, ReservationPaymentDto, ReservationState } from './types';
import { format } from "date-fns";
import { tryParseFloat } from '@/utils/utils';
import { PaymentTypes } from "./PaymentTypes/PaymentType";
import { PaymentTypeAdjustments } from "./PaymentTypeAdjustments/PaymentTypeAdjustment";
import { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import { deserializeReservationLogEntry } from "./ReservationLogEntry";

export function AdjustPaymentDialogComponent({ reservationState, payment, close }: AdjustPaymentDialogComponentProps) {

    const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    function paymentDateChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setPaymentDate(e.target.value);
    }

    const [adjustAmount, setAdjustAmount] = useState(payment.Amount);
    const [isAdjustAmountFocused, setIsAdjustAmountFocused] = useState(false);

    function adjustAmountChanged(e: React.ChangeEvent<HTMLInputElement>) {
        let value = tryParseFloat(e.target.value, adjustAmount);
        setAdjustAmount(value);
    }

    let hasFees = false;
    let feeAmount = 0;

    if (PaymentTypes.isStripePaymentType(payment.PaymentType)) {
        hasFees = true;
        feeAmount = payment.PaymentType.FeeAmount;
    } else if (PaymentTypes.isOmisePaymentType(payment.PaymentType)) {
        hasFees = true;
        feeAmount = payment.PaymentType.FeeAmount;
    } else if (PaymentTypes.isTwoC2PPaymentType(payment.PaymentType)) {
        hasFees = true;
        feeAmount = payment.PaymentType.FeeAmount;
    } else if (PaymentTypes.isWisePaymentType(payment.PaymentType)) {
        hasFees = true;
        feeAmount = payment.PaymentType.FeeAmount;
    }

    const [feesAmount, setFeesAmount] = useState(feeAmount);
    const [isFeesAmountFocused, setIsFeesAmountFocused] = useState(false);

    function feesAmountChanged(e: React.ChangeEvent<HTMLInputElement>) {
        let value = tryParseFloat(e.target.value, feesAmount);
        setFeesAmount(value);
    }

    const [adjustmentNote, setAdjustmentNote] = useState('');

    function adjustmentNoteChanged(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setAdjustmentNote(e.target.value);
    }

    const [adjustingPayment, setAdjustingPayment] = useState(false);

    async function adjustPayment() {
        setAdjustingPayment(true);

        let paymentTypeAdjustment: PaymentTypeAdjustments.PaymentTypeAdjustment | null = null;

        if (PaymentTypes.isStripePaymentType(payment.PaymentType)) {
            const stripePaymentTypeAdjustment = PaymentTypeAdjustments.StripePaymentTypeAdjustmentDefinition.create() as PaymentTypeAdjustments.StripePaymentTypeAdjustment;
            stripePaymentTypeAdjustment.FeeAmount = feesAmount;
            paymentTypeAdjustment = stripePaymentTypeAdjustment;
        } else if (PaymentTypes.isOmisePaymentType(payment.PaymentType)) {
            const omisePaymentTypeAdjustment = PaymentTypeAdjustments.OmisePaymentTypeAdjustmentDefinition.create() as PaymentTypeAdjustments.OmisePaymentTypeAdjustment;
            omisePaymentTypeAdjustment.FeeAmount = feesAmount;
            paymentTypeAdjustment = omisePaymentTypeAdjustment;
        } else if (PaymentTypes.isTwoC2PPaymentType(payment.PaymentType)) {
            const twoC2PPaymentTypeAdjustment = PaymentTypeAdjustments.TwoC2PPaymentTypeAdjustmentDefinition.create() as PaymentTypeAdjustments.TwoC2PPaymentTypeAdjustment;
            twoC2PPaymentTypeAdjustment.FeeAmount = feesAmount;
            paymentTypeAdjustment = twoC2PPaymentTypeAdjustment;
        } else if (PaymentTypes.isWisePaymentType(payment.PaymentType)) {
            const wisePaymentTypeAdjustment = PaymentTypeAdjustments.WisePaymentTypeAdjustmentDefinition.create() as PaymentTypeAdjustments.WisePaymentTypeAdjustment;
            wisePaymentTypeAdjustment.FeeAmount = feesAmount;
            paymentTypeAdjustment = wisePaymentTypeAdjustment;
        }

        const request: AdjustPaymentRequest = {
            effectiveDate: paymentDate,
            amount: adjustAmount,
            note: adjustmentNote,
            paymentTypeAdjustment: PaymentTypeAdjustments.serializePaymentTypeAdjustment(paymentTypeAdjustment)
        };

        const response = await fetch(
            `/reservations/${reservationState.ReservationId}/payments/${payment.PaymentId}/adjustments`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });

        if (response.ok) {
            const adjustPaymentResponse = await response.json() as AdjustPaymentResponse;

            if (adjustPaymentResponse.Payment) {
                reservationState.Payments.replace(
                    payment,
                    {
                        CreatedDate: new Date(adjustPaymentResponse.Payment.CreatedDate),
                        PaymentId: adjustPaymentResponse.Payment.PaymentId,
                        PaymentType: PaymentTypes.deserializePaymentType(adjustPaymentResponse.Payment.PaymentType),
                        Amount: adjustPaymentResponse.Payment.Amount,
                        Transactions: adjustPaymentResponse.Payment.Transactions.map(t => ({
                            CreatedDate: new Date(t.CreatedDate),
                            EffectiveDate: t.EffectiveDate,
                            Amount: t.Amount,
                            Note: t.Note,
                            TaxDocument: TaxDocumentTypes.deserializeTaxDocumentType(t.TaxDocument),
                        }))
                    });
            }

            reservationState.LogEntries.clear();

            adjustPaymentResponse.Logs
                .map(l => ({
                    LogEntryId: l.LogEntryId,
                    LogText: l.LogText,
                    LogData: deserializeReservationLogEntry(l.LogData),
                    CreatedBy: l.CreatedBy,
                    CreatedAt: l.CreatedAt
                }))
                .forEach(log => {
                    reservationState.LogEntries.add(log);
                });


            //reservationState.Payments.remove(payment);

            //if (PaymentTypes.isStripePaymentType(payment.PaymentType)) {
            //    payment.PaymentType.FeeAmount = feesAmount;
            //} else if (PaymentTypes.isOmisePaymentType(payment.PaymentType)) {
            //    payment.PaymentType.FeeAmount = feesAmount
            //} else if (PaymentTypes.isWisePaymentType(payment.PaymentType)) {
            //    payment.PaymentType.FeeAmount = feesAmount
            //}

            //reservationState.Payments.add({
            //    PaymentId: payment.PaymentId,
            //    PaymentType: payment.PaymentType,
            //    Amount: adjustAmount,
            //    Transactions: payment.Transactions
            //})





            await (window as any).salesSchedule.refresh();
        }

        setAdjustingPayment(false);

        close?.();
    }

    const isValid = paymentDate.length > 0 && adjustAmount >= 0;

    return (

        <div className="flex flex-col gap-2">
            <div>
                <table className="table">
                    <tbody>
                        <tr>
                            <th>Adjust Amount</th>
                            <td className="px-2 text-right" colSpan={2}>
                                <div className="flex flex-row place-content-end items-center gap-2 text-right">
                                    <input disabled={adjustingPayment} className="form-control w-32 text-right" type="number" min="0" step="0.01"
                                        value={isAdjustAmountFocused ? adjustAmount : adjustAmount.toFixed(2)}
                                        onChange={adjustAmountChanged}
                                        onFocus={() => setIsAdjustAmountFocused(true)}
                                        onBlur={() => {
                                            setIsAdjustAmountFocused(false);
                                            setAdjustAmount(Math.round((adjustAmount * 100)) / 100);
                                        }} />
                                    <div>THB</div>
                                </div>
                            </td>
                        </tr>
                        {hasFees && (
                            <tr>
                                <th>Collected Fees Amount</th>
                                <td className="px-2 text-right" colSpan={2}>
                                    <div className="flex flex-row place-content-end items-center gap-2 text-right">
                                        <input disabled={adjustingPayment} className="form-control w-32 text-right" type="number" min="0" step="0.01"
                                            value={isFeesAmountFocused ? feesAmount : feesAmount.toFixed(2)}
                                            onChange={feesAmountChanged}
                                            onFocus={() => setIsFeesAmountFocused(true)}
                                            onBlur={() => {
                                                setIsFeesAmountFocused(false);
                                                setFeesAmount(Math.round((feesAmount * 100)) / 100);
                                            }} />
                                        <div>THB</div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        <tr>
                            <th>Effective Date</th>
                            <td colSpan={2} className="place-items-end text-right">
                                <input disabled={adjustingPayment} type="date" className="form-control w-40" value={paymentDate} onChange={paymentDateChanged} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div>
                <label>Note</label>
                <textarea disabled={adjustingPayment} className="form-control" value={adjustmentNote} onChange={adjustmentNoteChanged}></textarea>
            </div>
            <div className="place-self-end">
                <button className="btn btn-outline-contrast" onClick={adjustPayment} disabled={adjustingPayment || !isValid}>Adjust Payment</button>
            </div>
        </div>
    );
}

export interface AdjustPaymentDialogComponentProps {
    reservationState: ReservationState;
    payment: ReservationPayment;
    close?: () => void;
}

export interface AdjustPaymentRequest {
    effectiveDate: string;
    amount: number;
    note: string;
    paymentTypeAdjustment: PaymentTypeAdjustments.PaymentTypeAdjustmentDto | null;
}

export interface AdjustPaymentResponse {
    Payment: ReservationPaymentDto;
    Logs: ReservationLogEntryContainerDto[];
}
